import { Button, Card, Flex, Popconfirm } from 'antd'
import { useNavigate } from 'react-router'
import { Invoice } from 'types'
import useDeleteInvoice from './hooks/useDeleteInvoice'
import useFinalizeInvoice from './hooks/useFinalizeInvoice'
import useMarkAsPaid from './hooks/useMarkAsPaid'
import useMarkAsUnpaid from './hooks/useMarkAsUnpaid'
import { useToast } from 'app/contexts/ToastContext'
import { makeErrorHandler } from 'shared/helpers/notificationHandlers'
import { routes } from 'app/routes'
import InvoiceStatusTags from 'features/invoices/components/InvoiceStatusTags'

interface InvoiceActionsProps {
  invoice: Invoice
}

const InvoiceActions = ({ invoice }: InvoiceActionsProps) => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const { deleteInvoice, isPending: isDeleting } = useDeleteInvoice({
    onSuccess: () => {
      toast.success({ message: 'Invoice deleted' })
      navigate('/')
    },
    onError: makeErrorHandler(
      toast,
      'Delete invoice',
      'Failed to delete invoice'
    ),
  })

  const { finalizeInvoice, isPending: isFinalizing } = useFinalizeInvoice({
    onSuccess: () =>
      toast.success({
        message: 'Invoice finalized',
        description: 'This invoice can no longer be edited.',
      }),
    onError: makeErrorHandler(
      toast,
      'Finalize invoice',
      'Failed to finalize invoice'
    ),
  })

  const { markAsPaid, isPending: isMarkingPaid } = useMarkAsPaid({
    onSuccess: () => toast.success({ message: 'Invoice marked as paid' }),
    onError: makeErrorHandler(
      toast,
      'Mark invoice as paid',
      'Failed to update payment status'
    ),
  })

  const { markAsUnpaid, isPending: isMarkingUnpaid } = useMarkAsUnpaid({
    onSuccess: () => toast.success({ message: 'Invoice marked as unpaid' }),
    onError: makeErrorHandler(
      toast,
      'Mark invoice as unpaid',
      'Failed to update payment status'
    ),
  })

  return (
    <Card>
      <h4 className="small text-uppercase fw-semibold mb-2 text-secondary">
        Status
      </h4>
      <InvoiceStatusTags invoice={invoice} gap="0.375rem" className="mb-3" />
      <h4 className="small text-uppercase fw-semibold mb-2 text-secondary">
        Actions
      </h4>
      <Flex vertical gap="0.5rem">
        {invoice.finalized ? (
          <>
            {!invoice.paid && (
              <Button
                type="primary"
                loading={isMarkingPaid}
                onClick={() => markAsPaid(invoice)}
                block
              >
                Mark as Paid
              </Button>
            )}
            {invoice.paid && (
              <Button
                type="primary"
                loading={isMarkingUnpaid}
                onClick={() => markAsUnpaid(invoice)}
                block
              >
                Mark as Unpaid
              </Button>
            )}
            <Button
              onClick={() =>
                navigate(`${routes.invoices.new}?from=${invoice.id}`)
              }
              block
            >
              Duplicate
            </Button>
            <p className="text-muted fst-italic m-0">
              Invoice is finalized — editing and deletion are locked
            </p>
          </>
        ) : (
          <>
            <Popconfirm
              title="Finalize this invoice?"
              description="Once finalized, the invoice can no longer be edited."
              onConfirm={() => finalizeInvoice(invoice)}
              okText="Finalize"
              cancelText="Cancel"
            >
              <Button
                type="primary"
                loading={isFinalizing}
                disabled={isFinalizing}
                block
              >
                Finalize
              </Button>
            </Popconfirm>
            <Button href={routes.invoices.edit(invoice.id)} block>
              Edit
            </Button>
            <Popconfirm
              title="Are you sure? This cannot be undone."
              onConfirm={() => deleteInvoice(invoice)}
              okText="OK"
              cancelText="Cancel"
            >
              <Button danger loading={isDeleting} block>
                Delete
              </Button>
            </Popconfirm>
          </>
        )}
      </Flex>
    </Card>
  )
}

export default InvoiceActions
